import type { FailCode } from '@packages/models/api';

import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { StyleSheet } from 'react-native';

import { ElectricalGridSelector } from './electrical-grid-selector';

const useElectricalGridStepTwoStyles = createUseStyles({
    buttonRow: {
        display: 'flex',
        gap: 15,
    },
    selectAreaLabel: {
        width: 500,
    },
    gridContainer: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 40,
    },
    zoomButtonSpacing: {
        marginRight: 8,
    },
    zoomControlsArea: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
});

const styles = StyleSheet.create({
    levelStyle: { width: 500 },
});

/**
 * Props for the {@link ElectricalGridStepTwo} component.
 */
export type ElectricalGridStepTwoProps = {
    /** The blob URL for the wiring diagram image. */
    diagramImageUrl: string;

    /** The x-axis range for the grid overlay (e.g. "A-M" or "N-Z"). */
    gridRange: string;

    /** Callback to submit the selected fail code. */
    onAddFailCode: () => void;

    /** Callback to navigate back to step 1. */
    onBack: () => void;

    /** Callback to close the modal. */
    onClose: () => void;

    /** Callback when the user selects a fail code by clicking a grid cell. */
    onGridFailCodeSelect: (failCode: FailCode) => void;

    /** Callback to zoom in on the diagram. */
    onZoomIn: () => void;

    /** Callback to zoom out on the diagram. */
    onZoomOut: () => void;

    /** The display name of the selected diagram. */
    selectedDiagramName: string;

    /** The current zoom offset in pixels. */
    zoom: number;
};

/**
 * Step 2 of the electrical grid lookup: view the wiring diagram image overlaid with a clickable
 * grid, and select a fail code by clicking a cell.
 *
 * @param props - {@link ElectricalGridStepTwoProps}
 * @returns the step two JSX
 */
/**
 * ElectricalGridStepTwo component renders the second step of the electrical grid lookup flow.
 * Displays the wiring diagram image with a clickable grid overlay for fail code selection.
 *
 * @param props - ElectricalGridStepTwoProps
 * @returns JSX.Element
 */
export function ElectricalGridStepTwo({
    diagramImageUrl,
    gridRange,
    onAddFailCode,
    onBack,
    onGridFailCodeSelect,
    onZoomIn,
    onZoomOut,
    selectedDiagramName,
    zoom,
    onClose,
}: ElectricalGridStepTwoProps) {
    // Hooks
    const classes = useElectricalGridStepTwoStyles();
    const { t } = useTranslation();

    // Forwarded callback aliases
    const handleAddFailCode = onAddFailCode;
    const handleBack = onBack;
    const handleGridFailCodeSelect = onGridFailCodeSelect;

    return (
        <>
            <Modal.Header closeButton>Lookup by Electrical Grid Location</Modal.Header>
            <Modal.Body>
                <Form>
                    <Row>
                        <Col>
                            <Label style={styles.levelStyle}>
                                Select the appropriate area on the {selectedDiagramName}:
                            </Label>
                        </Col>
                        <div className={classes.zoomControlsArea}>
                            <div className={classes.zoomButtonSpacing}>
                                <Button onPress={onZoomIn}>
                                    <Icon color="white" name="magnify-plus-outline" />
                                </Button>
                            </div>
                            <Button onPress={onZoomOut}>
                                <Icon color="white" name="magnify-minus-outline" />
                            </Button>
                        </div>
                    </Row>
                </Form>
                <div className={classes.gridContainer}>
                    <ElectricalGridSelector
                        range={gridRange}
                        updateFailCode={handleGridFailCodeSelect}
                        url={diagramImageUrl}
                        zoom={zoom}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <Button
                    iconLeft={<Icon color="blueOne" name="chevron-left" />}
                    onPress={handleBack}
                    variant="ghost-blue"
                >
                    {t('modals:failCodeSearch.actions.back', 'Back')}
                </Button>

                <div className={classes.buttonRow}>
                    <Button onPress={onClose} variant="link-blue">
                        <Typography color="blueOne">{t('qmr:sections:failCode.cancel')}</Typography>
                    </Button>
                    <Button onPress={handleAddFailCode} variant="primary">
                        {t('qmr:sections:failCode.addFailCode')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}

// ...existing code...
